// ── A document's own number (report c8ad7a51) ──
// A Chanote or a Nor Sor 3 Kor is told apart by the number printed on it, so those two types got a
// small box of their own next to the Document type, and the number goes into the file name right
// after the type word ("Chanote 53658 (Name) 2017-08-31.pdf"). The 4th opening put the same box on
// the other identity and registration papers, each taking only the characters its own number is
// printed with (the reporter: "Analyze each of the documents and allow number of letters and/or digits that
// are appropriate … Instead of adding any slashes, use a dash instead"), so the rules live in a table
// instead of one deed test. The list of types is built from the archive's own folder and file names,
// so a type is recognised by its NAME — by pattern, since a name may carry more words than the plain
// one ("Passport Copy", "Rental Accom. License") — and never by any id.
// Report 7dc8999f moved the table here out of the Upload Document wizard (components/DocStorage/
// UploadWizard.tsx), so the chat's document-upload group (components/Chat/ChatPanel.tsx) gives its own
// Number box exactly these rules: one copy, read by both.
export type NoRule = {
  test: RegExp;        // the Document type this rule is for
  drop: RegExp;        // everything this number is never printed with
  max: number;         // the box holds no more
  numeric?: boolean;   // digits only (the phone keypad on a tablet)
  oneLength?: boolean; // the number has ONE length (an ID card is 13 digits, never 12) — judges the AI's read
  lead?: string;       // and begins with this
  upper?: boolean;     // letters are printed as capitals
  fromNote?: boolean;  // the AI's note is a second place to look for it
  fromAddress?: boolean; // the number is the house number the address opens with — the read's houseNo, never its docNumber
};
const DEED_TYPE = /^chanote$|^nor\s*sor\s*(3|sam)\s*[gk]or$/i;
const PHOTOS = /\bphotos?\b/i;   // "Passport Photos" is the person, not a numbered paper
// First match wins, so the narrower names come first.
export const NO_RULES: NoRule[] = [
  { test: DEED_TYPE, drop: /\D/g, max: 5, numeric: true, fromNote: true },
  { test: /passport/i, drop: /[^a-z0-9]/gi, max: 10, upper: true },
  { test: /\bid\s*card\b/i, drop: /\D/g, max: 13, numeric: true, oneLength: true },
  // A company's own 13-digit number, printed in the top right-hand corner and always opening with a 0.
  // Its first half is the server's CARRIES_TAX_ID too (server/src/services/jobCompletionDocs.ts, report
  // 134da99f): the only papers a completed job files with the client's tax ID — change both together.
  { test: /company\s*(document|certificate)|tax\s*(id\b|identification)/i, drop: /\D/g, max: 13, numeric: true, oneLength: true, lead: '0' },
  { test: /bank\s*book/i, drop: /\D/g, max: 10, numeric: true, oneLength: true },
  // A house book is filed under the house number its address opens with, a '/' written as '-' (report 642f141b: "For
  // the house book instead of saving the 849 number … save the beginning of the address but do not use a slash. Instead
  // use a dash" — '86/11 Moo 5' files as 'House Book 86-11'), never its 11-digit registration number. Digits and dashes
  // of no one length; 12 still holds the 11 digits older names carry, so nameWithoutNo reads both. No phone keypad: it has no dash.
  // Its `test` is the server's HOUSE_BOOK too (server/src/services/docFolderPatterns.ts, the Filing pop-up's "Add each
  // document's own number", which reads the house number for it the same way) — change both together.
  { test: /house\s*book|tabi[ae]n\s*baan/i, drop: /[^0-9-]/g, max: 12, fromAddress: true },
  // A vehicle book is filed under its registration plate, which is Thai or Latin letters and digits.
  { test: /(car|motor\s*-?(bike|cycle))\s*book/i, drop: /[^\p{L}\p{N}-]/gu, max: 10, upper: true },
  { test: /(building|work)\s*permit|licen[cs]e/i, drop: /[^a-z0-9-]/gi, max: 20, upper: true },
];
/** The rule for this Document type, or null when the type carries no number of its own. */
export const noRuleFor = (type: string): NoRule | null => {
  const name = String(type || '').replace(/\s+/g, ' ').trim();
  if (!name || PHOTOS.test(name)) return null;
  return NO_RULES.find(r => r.test.test(name)) || null;
};
/** What the box keeps of what was typed, or of the AI's read: this type's own characters, a '/' as a '-'.
 *  A number that cannot even begin the way this one does (a company's always opens with a 0) is not
 *  kept at all, rather than trimmed into a different number. */
export const cleanNo = (raw: string, r: NoRule): string => {
  let v = String(raw || '').replace(/\//g, '-').replace(r.drop, '');
  if (r.upper) v = v.toUpperCase();
  if (r.lead && v && !v.startsWith(r.lead)) return '';
  return v.slice(0, r.max);
};
/** Which of the AI's reads fills this type's box: the house number off the address for a house book, else the document's own number. */
export const readNoFor = (r: NoRule, docNumber: string, houseNo: string): string => (r.fromAddress ? houseNo : docNumber) || '';
/** A number only half read is left for the operator to type: an ID card of 12 digits is no ID card. */
export const fitsNo = (v: string, r: NoRule): boolean => !!v && (!r.oneLength || v.length === r.max);
/** The box's width: a 5-digit deed stays as narrow as it was, a 20-character licence gets the room. */
export const noBoxW = (r: NoRule): number => (r.max <= 5 ? 96 : r.max <= 13 ? 124 : 150);
/** A new type takes the number with it, kept only as far as the new type's own rule allows — a file
 *  name can never show a number the type it is filed under does not carry. */
export const retypeNo = (no: string, type: string): string => {
  const r = noRuleFor(type);
  return r ? cleanNo(no, r) : '';
};
// ── The name without its number, for sorting (report 171a0879) ──
// Filing's Name sort compared whole file names, so "Passport PB… (<person>) …" stood among the passports in
// passport-NUMBER order, never in the person's ("ignore passport numbers, tax ID numbers, license numbers, etc.,
// when using the name sort"). This is the sort key: the name with the document's own number taken out, so the
// bracket — the person — decides. The number is the word right after the Document type's words, before any
// bracket, where that type carries a number (noRuleFor) and the word is exactly what the Number box would keep of
// it (cleanNo + fitsNo) with a digit in it — so "Passport Copy", "Nor Sor 3 Kor" or a date never count as one.
// Only the order is decided here; what the row shows is the name as it is.
const EXT = /\.[A-Za-z0-9]{1,6}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;   // the date the wizard ends a name with — never a number, even where no bracket precedes it
export const nameWithoutNo = (name: string): string => {
  const full = String(name || '');
  const ext = EXT.exec(full)?.[0] || '';
  const stem = ext ? full.slice(0, -ext.length) : full;
  const open = stem.indexOf('(');
  const head = open >= 0 ? stem.slice(0, open) : stem;   // the type and its number stand before any bracket
  const tail = open >= 0 ? stem.slice(open) : '';
  const words = head.split(/\s+/).filter(Boolean);
  for (let i = 1; i < words.length; i++) {
    const w = words[i];
    if (!/\p{N}/u.test(w) || ISO_DATE.test(w)) continue;
    const r = noRuleFor(words.slice(0, i).join(' '));
    if (!r) continue;
    const kept = cleanNo(w, r);
    if (kept.toLowerCase() !== w.toLowerCase() || !fitsNo(kept, r)) continue;
    const rest = [...words.slice(0, i), ...words.slice(i + 1)].join(' ');
    return `${rest}${tail ? ` ${tail}` : ''}${ext}`;
  }
  return full;
};
